import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Table, Button, OverlayTrigger, Tooltip, Badge, Modal, Spinner } from 'react-bootstrap';
import { FaPlus, FaEdit, FaSearch, FaUndo, FaEye, FaEyeSlash, FaTrash, FaUpload, FaUser, FaCopy } from "react-icons/fa";
import APIServicenew from "../../utils/APIGeneralService";
import { UserContext } from "../../context/UserContextProvider";
import { ToastContainer, toast } from "react-toastify";
import StudentFilter from "./StudentFilter";
import useMenuPermissions from "../../components/CustomHook/custompermission";
import { formateDatemdy } from "../../utils/Helper";
import Paginator from "../../components/common/Pagination";
import DeleteModal from "../../components/common/DeleteModel";

const ManageStudent = () => {
  const permissionscontext = useMenuPermissions("/admin/student");
  const [studentList, setStudentList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [count, setCount] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDt, setShowDt] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importGradeId, setImportGradeId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [grades, setGrades] = useState([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [currentSchoolId, setCurrentSchoolId] = useState(null);

  const navigate = useNavigate();
  const limit = 10;

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout, data } = useContext(UserContext);

  const headers = {
    "Content-Type": "application/json",
    "auth-token": authToken,
    "authorization": accessToken
  };

  useEffect(() => {
    fetchStudents();
  }, [currentPage, filters]);

  // Fetch current school ID for school admin users
  useEffect(() => {
    if (data?.user_type === "school") {
      fetchCurrentSchool();
    }
  }, [data]);

  const fetchCurrentSchool = async () => {
    try {
      const response = await APIServices.get(
        `/admin/school/short_list`,
        headers
      );
      if (response?.data?.status === true) {
        const schoolsData = response?.data?.data?.data || [];
        const userSchool = schoolsData.find(school => school.admin_id === data?.id);
        if (userSchool) {
          setCurrentSchoolId(userSchool.id);
        }
      }
    } catch (error) {
      console.error("Error fetching school:", error);
    }
  };

  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      let url = `/admin/cms/grade_list?status=Active&limit=100`;
      
      // If school admin, send school_id in the API call
      if (data?.user_type === "school" && currentSchoolId) {
        url = `/admin/cms/grade_list?skip=0&limit=1000&school_id=${currentSchoolId}`;
      }
      
      const response = await APIServices.get(url, headers);
      
      if (response?.data?.status === true) {
        const gradesData = response?.data?.data?.grades || response?.data?.data?.data || [];
        setGrades(gradesData);
      }
    } catch (error) {
      console.error("Error fetching grades:", error);
    } finally {
      setLoadingGrades(false);
    }
  };

  useEffect(() => {
    if (showImportModal) {
      fetchGrades();
    }
  }, [showImportModal, currentSchoolId]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (currentPage - 1) * limit,
        limit: limit,
      };

      // Add search parameter from filters
      if (filters.search && filters.search.trim() !== "") {
        params.search = filters.search.trim();
      }

      if (filters.email && filters.email.trim() !== "") {
        params.email = filters.email.trim();
      }

      if (filters.mobile && filters.mobile.trim() !== "") {
        params.mobile = filters.mobile.trim();
      }

      if (filters.grade_id && filters.grade_id !== "") {
        params.grade_id = filters.grade_id;
      }

      if (filters.school_id && filters.school_id !== "") {
        params.school_id = filters.school_id;
      }

      if (filters.course_id && filters.course_id !== "") {
        params.course_id = filters.course_id;
      }

      if (filters.status && filters.status !== "") {
        params.status = filters.status;
      }

      const queryString = new URLSearchParams(params).toString();

      const response = await APIServices.get(
        `/admin/student/list?${queryString}`,
        headers
      );

      if (response?.data?.status === true) {
        setStudentList(response?.data?.data?.data || []);
        const total = response?.data?.data?.total || 0;
        setCount(total);
        setTotalCount(Math.ceil(total / limit) || 0);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        setStudentList([]);
        setCount(0);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      showToast("Error fetching students", "error");
      setStudentList([]);
      setCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (student) => {
    const newStatus = student.status === "Active" ? "Inactive" : "Active";

    const formData = new FormData();
    formData.append("id", student.id);
    formData.append("status", newStatus);

    try {
      const res = await APIServices.put(
        `/admin/student/status`,
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Status updated successfully", "success");
        fetchStudents();
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Error updating status", "error");
    }
  };

  const handleDeleteStudent = async () => {
    try {
      if (selectedStudent !== null) {
        const formData = new FormData();
        formData.append("id", selectedStudent);

        const res = await APIServices.delete(
          `/admin/student/delete`,
          formData,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );

        if (res?.data?.status === true) {
          showToast(res?.data?.message || "Student deleted successfully", "success");
          if (studentList.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          } else {
            fetchStudents();
          }
          setSelectedStudent(null);
        } else {
          showToast(res?.data?.message || "Something went wrong.", "error");
        }
      }
      setShowDt(false);
    } catch (error) {
      console.error("Error deleting student:", error);
      showToast("Error deleting student", "error");
      setShowDt(false);
    }
  };
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleApplyFilter = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setFilters({});
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || file.type === "application/vnd.ms-excel")) {
      setImportFile(file);
    } else {
      showToast("Please select a valid Excel file (.xlsx or .xls)", "error");
      e.target.value = "";
    }
  };

  const handleDownloadSample = () => {
    const sampleUrl = "/images/sample-student-import.xlsx";
    window.open(sampleUrl, "_blank");
  };

  const handleBulkImport = async () => {
    if (!importGradeId) {
      showToast("Please select a grade", "error");
      return;
    }
    if (!importFile) {
      showToast("Please select an Excel file", "error");
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append("grade_id", importGradeId);
      formData.append("file", importFile);
      
      // If school admin, also send school_id
      if (data?.user_type === "school" && currentSchoolId) {
        formData.append("school_id", currentSchoolId);
      }

      const response = await APIServices.post(
        "/admin/student/bulk-import",
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.data?.status === true) {
        showToast(response?.data?.message || "Students imported successfully", "success");
        setShowImportModal(false);
        setImportGradeId("");
        setImportFile(null);
        fetchStudents(); // Refresh the list
      } else {
        showToast(response?.data?.message || "Import failed", "error");
      }
    } catch (error) {
      console.error("Error importing students:", error);
      showToast(error.response?.data?.message || "Error importing students", "error");
    } finally {
      setImportLoading(false);
    }
  };

  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  const getSerialNumber = (index) => {
    return (currentPage - 1) * limit + index + 1;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge bg="success">Active</Badge>;
      case 'Inactive':
        return <Badge bg="danger">Inactive</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleCopyPassword = (password) => {
    navigator.clipboard.writeText(password);
    showToast("Password copied to clipboard", "success");
  };

  const hasActiveFilters = Object.keys(filters).some(key => filters[key] && filters[key] !== "");

  const getFilterDisplayText = () => {
    const activeFilters = [];
    if (filters.search) activeFilters.push(`Search: ${filters.search}`);
    if (filters.email) activeFilters.push(`Email: ${filters.email}`);
    if (filters.mobile) activeFilters.push(`Mobile: ${filters.mobile}`);
    if (filters.grade_id) activeFilters.push(`Grade: ${filters.grade_id}`);
    if (filters.school_id) activeFilters.push(`School: ${filters.school_id}`);
    if (filters.course_id) activeFilters.push(`Course: ${filters.course_id}`);
    if (filters.status) activeFilters.push(`Status: ${filters.status}`);
    return activeFilters.join(", ");
  };

  return (
    <>
      <div className="momList_inner todo_list_inner student_list_inner">
        <div className="mom_header d-flex align-items-center justify-content-between mb-3">
          <h1 className="font_blue_18 font-600">
            {`Student Management (${count})`}
          </h1>

          <ul className="d-flex align-items-center mom_filter">
            <li
              className="d-flex align-items-center cursor-pointer"
              onClick={() => setShowFilter(true)}
            >
              Filter By{" "}
              <img
                src="/images/Sort.svg"
                className="img-fluid ms-2"
                alt="Sort"
              />
            </li>
            <li>
              {permissionscontext.add == 1 && (
                <button
                  className="btn red_button d-flex align-items-center"
                  onClick={() => navigate("/admin/add-student")}
                >
                  <FaPlus className="me-1" />
                  Add Student
                </button>
              )}
            </li>
            {data?.user_type === "school" && (
              <li className="ms-2">
                <button
                  className="btn btn-success blue_button d-flex align-items-center"
                  onClick={() => setShowImportModal(true)}
                >
                  <FaUpload className="me-1" />
                  Import Student
                </button>
              </li>
            )}
          </ul>
        </div>
        <div className="todoList_table">
          <div className="table-responsive">
            <Table className="table">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '3%' }}>#</th>
                  <th style={{ width: '8%' }}>Profile</th>
                  <th style={{ width: '8%' }}>Roll Number</th>
                  <th style={{ width: '12%' }}>Student Name</th>
                  <th style={{ width: '12%' }}>Father Name</th>
                  <th style={{ width: '12%' }}>School</th>
                  <th style={{ width: '12%' }}>Email</th>
                  <th style={{ width: '8%' }}>Mobile</th>
                  <th style={{ width: '8%' }}>Grade</th>
                  {/* <th style={{ width: '8%' }}>Password</th> */}
                  <th style={{ width: '6%' }}>Status</th>
                  <th style={{ width: '10%' }}>Created Date</th>
                  <th style={{ width: '8%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : studentList.length > 0 ? (
                  studentList.map((student, index) => (
                    <tr key={student.id}>
                      <td>{getSerialNumber(index)}</td>
                      <td>
                        <div className="text-center">
                          {student.profile_pic ? (
                            <img
                              src={student.profile_pic}
                              alt={student.first_name}
                              onError={(e) => { e.target.src = "/images/placeholder-img.jpg"; }}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '50%',
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '50px',
                                height: '50px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 10px auto'
                              }}
                            >
                              <FaUser size={25} color="#999" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <strong>{student.roll_number}</strong>
                      </td>
                      <td>
                        {student.first_name} {student.last_name}
                      </td>
                      <td>
                        {student.father_name}
                      </td>
                      <td>
                        <div>
                          <strong>{student.school_name}</strong>
                          <br />
                          <small className="text-muted">({student.school_code})</small>
                        </div>
                      </td>
                      <td>{student.email}</td>
                      <td>{student.country_code}-{student.mobile}</td>
                      <td>
                        <Badge bg="info">
                          {student.grade_name}
                        </Badge>
                      </td>
                      {/* <td>
                        <div className="d-flex align-items-center">
                          <span className="font-monospace text-muted me-1">
                            {student.password
                              ? visiblePasswords[student.id]
                                ? student.password
                                : "••••••••"
                              : "—"}
                          </span>
                          {student.password && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-1 pt-0 text-muted d-inline-flex align-items-center justify-content-center border-0"
                              onClick={() =>
                                setVisiblePasswords((prev) => ({
                                  ...prev,
                                  [student.id]: !prev[student.id],
                                }))
                              }
                            >
                              {visiblePasswords[student.id] ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                            </button>
                          )}
                          {student.password && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-1 pt-0 text-muted d-inline-flex align-items-center justify-content-center border-0"
                              onClick={() => handleCopyPassword(student.password)}
                              title="Copy password"
                            >
                              <FaCopy size={14} />
                            </button>
                          )}
                        </div>
                      </td> */}
                      <td>
                        <Form className="Switch_toggle">
                          <input
                            type="checkbox"
                            id={`switch-${student.id}`}
                            checked={student.status === "Active"}
                            onChange={() => handleStatusChange(student)}
                            disabled={!permissionscontext.edit}
                          />
                          <label
                            className="position-relative"
                            htmlFor={`switch-${student.id}`}
                          >
                            Toggle
                          </label>
                        </Form>
                      </td>
                      <td>{formateDatemdy(student.created_at)}</td>
                      <td>
                        <ul className="d-flex align-items-center list-unstyled mb-0">
                          <li className="me-2">
                            <OverlayTrigger placement="top" overlay={<Tooltip>View Details</Tooltip>}>
                              <button
                                className="btn btn-sm btn-outline-info py-1"
                                onClick={() => {
                                  navigate(`/admin/student-detail/${student.id}`, {
                                    state: { row: student },
                                  });
                                }}
                              >
                                <FaEye />
                              </button>
                            </OverlayTrigger>
                          </li>
                          <li className="me-2">
                            {permissionscontext.edit == 1 && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Edit Student</Tooltip>}>
                                <button
                                  className="btn btn-sm btn-outline-success py-1"
                                  onClick={() => {
                                    navigate(`/admin/update-student/${student.id}`, {
                                      state: { row: student },
                                    });
                                  }}
                                >
                                  <FaEdit />
                                </button>
                              </OverlayTrigger>
                            )}
                          </li>
                          <li>
                            {permissionscontext.delete == 1 && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Delete Student</Tooltip>}>
                                <button
                                  className="btn btn-sm btn-outline-danger py-1"
                                  onClick={() => {
                                    setSelectedStudent(student.id);
                                    setShowDt(true);
                                  }}
                                >
                                  <FaTrash />
                                </button>
                              </OverlayTrigger>
                            )}
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      <div className="text-muted">
                        <h5>No Students found</h5>
                        <p>
                          {hasActiveFilters
                            ? "Try clearing filters"
                            : "Click on 'Add Student' to create your first student"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <div className="pagination_box mt-4">
          {count > limit && (
            <Paginator
              currentPage={currentPage}
              totalPages={totalCount}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilter && (
        <StudentFilter
          appliedFilters={filters}
          show={showFilter}
          onHide={() => setShowFilter(false)}
          onApplyFilter={handleApplyFilter}
          onClear={handleClearFilter}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        showdt={showDt}
        handleCloseDt={() => setShowDt(false)}
        handledelete={handleDeleteStudent}
      />

      {/* Import Student Modal */}
      <Modal
        show={showImportModal}
        onHide={() => {
          setShowImportModal(false);
          setImportGradeId("");
          setImportFile(null);
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Import Students</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Grade <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={importGradeId}
                onChange={(e) => setImportGradeId(e.target.value)}
                disabled={loadingGrades}
              >
                <option value="">Select Grade</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </Form.Select>
              {loadingGrades && <Spinner animation="border" size="sm" className="ms-2" />}
              {data?.user_type === "school" && currentSchoolId && (
                <Form.Text className="text-muted d-block mt-1">
                  Showing grades for your school only
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Upload Excel File <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFileChange}
              />
              <Form.Text className="text-muted">
                Please upload .xlsx or .xls file format
              </Form.Text>
            </Form.Group>

            <div className="text-center">
              <Button
                variant="link"
                onClick={handleDownloadSample}
                className="p-0"
              >
                Download Sample File
              </Button>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowImportModal(false);
              setImportGradeId("");
              setImportFile(null);
            }}
            disabled={importLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleBulkImport}
            disabled={importLoading}
          >
            {importLoading ? "Importing..." : "Import Students"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </>
  );
};

export default ManageStudent;